// Switch (DESIGN §3.6.8): Spur 40 × 24, aus `pill-card` mit Daumen `fg`, an `accent-strong` mit
// Daumen `on-accent-strong`, Daumen gleitet 150 ms. Label + Beschreibung; gesperrte Pflicht-Schalter
// mit Schloss und Tooltip „Required“.
import { useId, type ReactNode, type Ref } from 'react';
import { LockSimpleIcon } from '@phosphor-icons/react';
import { Switch as SwitchPrimitive } from 'radix-ui';
import { useFieldProps } from './_lib/fieldContext';
import { Tooltip } from './Tooltip';
import { cn } from '../basis/kern';

export interface SwitchProps {
  ref?: Ref<HTMLButtonElement>;
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  /** Label links (Einstellungszeilen) oder rechts (Standard). */
  labelPosition?: 'start' | 'end';
  disabled?: boolean;
  /** Gesperrt mit Schloss und Tooltip (Standard „Required“). */
  locked?: boolean | string;
  name?: string;
  id?: string;
  'aria-label'?: string;
  className?: string;
}

export function Switch({
  ref,
  label,
  description,
  labelPosition = 'end',
  disabled,
  locked,
  className,
  id,
  ...rest
}: SwitchProps) {
  const autoId = useId();
  const field = useFieldProps({ id });
  const switchId = field.id ?? `switch${autoId}`;
  const descriptionId = `${switchId}-desc`;
  const isLocked = Boolean(locked);
  const lockReason = typeof locked === 'string' ? locked : 'Required';
  const control = (
    <SwitchPrimitive.Root
      {...rest}
      ref={ref}
      id={switchId}
      disabled={disabled}
      // Gesperrt bleibt fokussierbar (aria-disabled), damit der Tooltip den Grund nennt (DESIGN §3.0).
      aria-disabled={isLocked || undefined}
      onClick={isLocked ? (event) => event.preventDefault() : undefined}
      aria-describedby={
        cn(field['aria-describedby'], description ? descriptionId : undefined) || undefined
      }
      className={cn(
        'group/switch relative inline-flex h-6 w-10 shrink-0 items-center rounded-full bg-pill-card p-0.5',
        'transition-colors duration-(--bar-dur-fast) ease-standard data-[state=checked]:bg-accent-strong',
        'disabled:cursor-not-allowed disabled:opacity-60 aria-disabled:cursor-not-allowed aria-disabled:opacity-60',
        // Trefferfläche 40 hoch, unsichtbar erweitert.
        'before:absolute before:-inset-y-2 before:inset-x-0',
      )}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'block size-5 rounded-full bg-fg transition-transform duration-(--bar-dur-fast) ease-standard',
          'data-[state=checked]:translate-x-4 data-[state=checked]:bg-on-accent-strong',
        )}
      />
    </SwitchPrimitive.Root>
  );
  const lockedControl = isLocked ? (
    <span className="inline-flex items-center gap-1.5 text-fg-secondary">
      <LockSimpleIcon size={16} aria-hidden="true" />
      <Tooltip content={lockReason}>{control}</Tooltip>
    </span>
  ) : (
    control
  );
  if (!label) return <span className={className}>{lockedControl}</span>;
  return (
    <div
      className={cn(
        'flex items-start gap-3',
        labelPosition === 'start' && 'flex-row-reverse justify-between',
        className,
      )}
    >
      <span className="pt-px">{lockedControl}</span>
      <span className="flex min-w-0 flex-col">
        <label
          htmlFor={switchId}
          className={cn('text-body', disabled || isLocked ? 'text-fg-secondary' : 'text-fg')}
        >
          {label}
        </label>
        {description ? (
          <span id={descriptionId} className="text-label text-fg-secondary">
            {description}
          </span>
        ) : null}
      </span>
    </div>
  );
}
